// src/pages/CRM/Core/AccountDetail.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  IconButton,
  Chip,
  Avatar,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemAvatar,
  Card,
  CardContent,
  CardActionArea,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  LinkedIn as LinkedInIcon,
  Twitter as TwitterIcon,
  Facebook as FacebookIcon,
  Language as WebsiteIcon,
  Add as AddIcon,
  Note as NoteIcon,
  Event as MeetingIcon,
  PhoneCallback as CallIcon,
  Person as PersonIcon,
  AccountTree as HierarchyIcon,
  AttachMoney as RevenueIcon,
  Group as EmployeesIcon,
  CalendarToday as FoundedIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import {
  AccountType,
  AccountTypeLabels,
  AccountTypeColors,
  AccountStatus,
  AccountStatusLabels,
  Industries,
  ActivityType,
  ActivityTypeLabels,
} from './crmEnums';

// Tab Panel Component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const AccountDetail = () => {
  const navigate = useNavigate();
  const { chatbotId, accountId } = useParams();
  const [searchParams] = useSearchParams();
  const initialEditMode = searchParams.get('edit') === 'true';

  // Permission state
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  // State
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(initialEditMode);
  const [tabValue, setTabValue] = useState(0);
  const [allAccounts, setAllAccounts] = useState([]);
  const [tags, setTags] = useState([]);

  // Form state
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [deleteActivityId, setDeleteActivityId] = useState(null);
  const [activityForm, setActivityForm] = useState({
    activity_type: ActivityType.NOTE,
    subject: '',
    description: '',
    activity_date: new Date().toISOString().slice(0, 16),
    // Type-specific fields
    email_from: '',
    email_to: '',
    email_cc: '',
    call_direction: 'outbound',
    call_result: '',
    duration_minutes: '',
    task_due_date: '',
    task_priority: 'medium',
  });

  // Check permissions on mount
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const view = await hasPermission('crm', 'view');
        const edit = await hasPermission('crm', 'edit');
        const del = await hasPermission('crm', 'delete');
        
        setCanView(view);
        setCanEdit(edit);
        setCanDelete(del);
      } catch (err) {
        console.error("Permission check failed", err);
      } finally {
        setPermissionsChecked(true);
      }
    };
    checkPermissions();
  }, []);

  // Fetch account details
  const fetchAccount = useCallback(async () => {
    if (!permissionsChecked) return;
    if (!canView) {
        setLoading(false);
        return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`/api/crm/accounts/${accountId}`);

      if (response.data.success) {
        const accountData = response.data.data || response.data.account;
        setAccount(accountData);
        // Map nested backend data to flat form structure for editing
        setFormData({
          name: accountData.name || '',
          account_type: accountData.account_type || AccountType.PROSPECT,
          status: accountData.status || AccountStatus.ACTIVE,
          industry: accountData.industry || '',
          sub_industry: accountData.sub_industry || '',
          annual_revenue: accountData.annual_revenue || '',
          employee_count: accountData.employee_count || '',
          founded_year: accountData.founded_year || '',
          website: accountData.website || '',
          phone: accountData.phone || '',
          fax: accountData.fax || '',
          email: accountData.email || '',
          tax_id: accountData.tax_id || '',
          registration_number: accountData.registration_number || '',
          parent_account_id: accountData.parent_account_id || '',
          // Handle nested address objects
          billing_street: accountData.billing_address?.street || '',
          billing_city: accountData.billing_address?.city || '',
          billing_state: accountData.billing_address?.state || '',
          billing_postal_code: accountData.billing_address?.postal_code || '',
          billing_country: accountData.billing_address?.country || '',
          shipping_street: accountData.shipping_address?.street || '',
          shipping_city: accountData.shipping_address?.city || '',
          shipping_state: accountData.shipping_address?.state || '',
          shipping_postal_code: accountData.shipping_address?.postal_code || '',
          shipping_country: accountData.shipping_address?.country || '',
          // Handle nested social object
          linkedin_url: accountData.social?.linkedin_url || '',
          twitter_handle: accountData.social?.twitter_handle || '',
          facebook_url: accountData.social?.facebook_url || '',
          description: accountData.description || '',
          tags: accountData.tags || [],
        });
      } else {
        setError(response.data.message || 'Failed to fetch account');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch account');
    } finally {
      setLoading(false);
    }
  }, [permissionsChecked, canView, accountId]);

  // Fetch all accounts for parent selection
  const fetchAllAccounts = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/accounts?per_page=100');
      if (response.data.success) {
        setAllAccounts(response.data.data?.filter((a) => a.id !== parseInt(accountId)) || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  // Fetch tags
  const fetchTags = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/tags');
      if (response.data.success) {
        setTags(response.data.data?.filter((t) => t.applies_to_accounts) || []);
      }
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  useEffect(() => {
    if (permissionsChecked && canView) {
        fetchAccount();
        fetchAllAccounts();
        fetchTags();
    } else if (permissionsChecked && !canView) {
        setLoading(false);
    }
  }, [fetchAccount, permissionsChecked, canView]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFormChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const handleTagToggle = (tagName) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.includes(tagName)
        ? prev.tags.filter((t) => t !== tagName)
        : [...prev.tags, tagName],
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name?.trim()) {
      errors.name = 'Account name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        parent_account_id: formData.parent_account_id || null,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        founded_year: formData.founded_year ? parseInt(formData.founded_year) : null,
      };

      const response = await axiosInstance.put(
        `/api/crm/accounts/${accountId}`,
        payload
      );

      if (response.data.success) {
        setEditMode(false);
        fetchAccount();
      } else {
        setFormErrors({ submit: response.data.message || 'Failed to update account' });
      }
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to update account',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    fetchAccount(); // Re-fetch to reset form data
    setFormErrors({});
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await axiosInstance.delete(`/api/crm/accounts/${accountId}`);

      if (response.data.success) {
        navigate(`/chatbot/${chatbotId}/crm/clients`);
      } else {
        setError(response.data.message || 'Failed to delete account');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete account');
    }
    setDeleteDialogOpen(false);
  };

  // Activity handlers
  const handleActivityFormChange = (field) => (event) => {
    setActivityForm((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const resetActivityForm = () => {
    setActivityForm({
      activity_type: ActivityType.NOTE,
      subject: '',
      description: '',
      activity_date: new Date().toISOString().slice(0, 16),
      email_from: '', email_to: '', email_cc: '',
      call_direction: 'outbound', call_result: '', duration_minutes: '',
      task_due_date: '', task_priority: 'medium',
    });
    setEditingActivity(null);
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setActivityForm({
      activity_type: activity.activity_type || ActivityType.NOTE,
      subject: activity.subject || '',
      description: activity.description || '',
      activity_date: activity.activity_date
        ? new Date(activity.activity_date).toISOString().slice(0, 16) : '',
      email_from: activity.email_from || '',
      email_to: activity.email_to || '',
      email_cc: activity.email_cc || '',
      call_direction: activity.call_direction || 'outbound',
      call_result: activity.call_result || '',
      duration_minutes: activity.duration_minutes || '',
      task_due_date: activity.task_due_date
        ? new Date(activity.task_due_date).toISOString().slice(0, 16) : '',
      task_priority: activity.task_priority || 'medium',
    });
    setActivityDialogOpen(true);
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      const response = await axiosInstance.delete(`/api/crm/activities/${activityId}`);
      if (response.data.success) {
        fetchAccount();
      }
    } catch (err) {
      console.error('Failed to delete activity:', err);
    }
    setDeleteActivityId(null);
  };

  const handleAddActivity = async () => {
    try {
      const payload = {
        activity_type: activityForm.activity_type,
        subject: activityForm.subject,
        description: activityForm.description,
        activity_date: activityForm.activity_date,
        account_id: parseInt(accountId),
      };

      // Add type-specific fields
      if (activityForm.activity_type === ActivityType.EMAIL) {
        payload.email_from = activityForm.email_from;
        payload.email_to = activityForm.email_to;
        payload.email_cc = activityForm.email_cc;
      } else if (activityForm.activity_type === ActivityType.CALL) {
        payload.call_direction = activityForm.call_direction;
        payload.call_result = activityForm.call_result;
        payload.duration_minutes = activityForm.duration_minutes
          ? parseInt(activityForm.duration_minutes) : null;
      } else if (activityForm.activity_type === ActivityType.TASK) {
        payload.task_due_date = activityForm.task_due_date || null;
        payload.task_priority = activityForm.task_priority;
      }

      let response;
      if (editingActivity) {
        response = await axiosInstance.put(
          `/api/crm/activities/${editingActivity.id}`, payload
        );
      } else {
        response = await axiosInstance.post('/api/crm/activities', payload);
      }

      if (response.data.success) {
        setActivityDialogOpen(false);
        resetActivityForm();
        fetchAccount();
      }
    } catch (err) {
      console.error('Failed to save activity:', err);
    }
  };

  // Helper functions
  const getInitials = (name) => {
    if (!name) return '?';
    const words = name.split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // --- FIXED: Ensure Absolute URL Helper ---
  const ensureAbsoluteUrl = (url) => {
    if (!url) return '';
    let formattedUrl = url.trim();
    // Check if url starts with http:// or https://
    if (!/^https?:\/\//i.test(formattedUrl)) {
      return `https://${formattedUrl}`;
    }
    return formattedUrl;
  };
  // ------------------------------------------

  const formatRevenue = (revenue) => {
    if (!revenue) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(revenue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityIcon = (type) => {
    const icons = {
      [ActivityType.EMAIL]: <EmailIcon />,
      [ActivityType.CALL]: <CallIcon />,
      [ActivityType.MEETING]: <MeetingIcon />,
      [ActivityType.NOTE]: <NoteIcon />,
    };
    return icons[type] || <NoteIcon />;
  };

  if (!permissionsChecked || loading) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 10 } }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">
          You do not have permission to view this account.
        </Alert>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients`)}
          sx={{ mt: 2 }}
        >
          Back to Accounts
        </Button>
      </Box>
    );
  }

  if (!account) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="warning">Account not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients`)}
          sx={{ mt: 2 }}
        >
          Back to Accounts
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}
          >
            CRM
          </Link>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients`)}
          >
            Clients
          </Link>
          <Typography color="text.primary">{account.name}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients`)}>
              <ArrowBackIcon />
            </IconButton>
            <Avatar
              src={account.logo_url}
              sx={{ width: 64, height: 64, bgcolor: 'secondary.main', fontSize: '1.5rem' }}
            >
              {getInitials(account.name)}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                {account.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={AccountTypeLabels[account.account_type]}
                  size="small"
                  color={AccountTypeColors[account.account_type]}
                  variant="outlined"
                />
                <Chip
                  label={AccountStatusLabels[account.status]}
                  size="small"
                  color={account.status === AccountStatus.ACTIVE ? 'success' : 'default'}
                />
                {account.industry && (
                  <Chip label={account.industry} size="small" variant="outlined" />
                )}
              </Box>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {editMode ? (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={handleCancelEdit}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  Save Changes
                </Button>
              </>
            ) : (
              <>
                {canEdit && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setEditMode(true)}
                  >
                    Edit
                  </Button>
                )}
                {canDelete && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </Stack>
        </Box>
      </Box>

      {formErrors.submit && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setFormErrors({})}>
          {formErrors.submit}
        </Alert>
      )}

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <PersonIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5">{account.contacts?.length || 0}</Typography>
            <Typography variant="body2" color="text.secondary">Contacts</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <RevenueIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5">{formatRevenue(account.annual_revenue)}</Typography>
            <Typography variant="body2" color="text.secondary">Annual Revenue</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <EmployeesIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5">{account.employee_count || 'N/A'}</Typography>
            <Typography variant="body2" color="text.secondary">Employees</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <FoundedIcon color="primary" sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="h5">{account.founded_year || 'N/A'}</Typography>
            <Typography variant="body2" color="text.secondary">Founded</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Details" />
          <Tab label="Contacts" />
          <Tab label="Hierarchy" />
          <Tab label="Activities" />
        </Tabs>
      </Paper>

      {/* Details Tab */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Company Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Company Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Account Name"
                      value={formData.name}
                      onChange={handleFormChange('name')}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Account Type</InputLabel>
                      <Select
                        value={formData.account_type}
                        label="Account Type"
                        onChange={handleFormChange('account_type')}
                      >
                        {Object.values(AccountType).map((type) => (
                          <MenuItem key={type} value={type}>
                            {AccountTypeLabels[type]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={formData.status}
                        label="Status"
                        onChange={handleFormChange('status')}
                      >
                        {Object.values(AccountStatus).map((status) => (
                          <MenuItem key={status} value={status}>
                            {AccountStatusLabels[status]}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Industry</InputLabel>
                      <Select
                        value={formData.industry}
                        label="Industry"
                        onChange={handleFormChange('industry')}
                      >
                        <MenuItem value="">Select Industry</MenuItem>
                        {Industries.map((industry) => (
                          <MenuItem key={industry} value={industry}>
                            {industry}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Sub-Industry"
                      value={formData.sub_industry}
                      onChange={handleFormChange('sub_industry')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Annual Revenue"
                      type="number"
                      value={formData.annual_revenue}
                      onChange={handleFormChange('annual_revenue')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Employee Count"
                      type="number"
                      value={formData.employee_count}
                      onChange={handleFormChange('employee_count')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Founded Year"
                      type="number"
                      value={formData.founded_year}
                      onChange={handleFormChange('founded_year')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  <ListItem>
                    <ListItemIcon><BusinessIcon /></ListItemIcon>
                    <ListItemText 
                      primary={AccountTypeLabels[account.account_type]} 
                      secondary="Account Type" 
                    />
                  </ListItem>
                  {account.industry && (
                    <ListItem>
                      <ListItemIcon><BusinessIcon /></ListItemIcon>
                      <ListItemText 
                        primary={account.industry} 
                        secondary="Industry" 
                      />
                    </ListItem>
                  )}
                  {account.website && (
                    <ListItem 
                      button 
                      component="a" 
                      href={ensureAbsoluteUrl(account.website)} 
                      target="_blank"
                    >
                      <ListItemIcon><WebsiteIcon /></ListItemIcon>
                      <ListItemText primary={account.website} secondary="Website" />
                    </ListItem>
                  )}
                  {account.phone && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary={account.phone} secondary="Phone" />
                    </ListItem>
                  )}
                  {account.email && (
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText primary={account.email} secondary="Email" />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Website"
                      value={formData.website}
                      onChange={handleFormChange('website')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone"
                      value={formData.phone}
                      onChange={handleFormChange('phone')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Fax"
                      value={formData.fax}
                      onChange={handleFormChange('fax')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Email"
                      type="email"
                      value={formData.email}
                      onChange={handleFormChange('email')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {account.website && (
                    <ListItem 
                      button 
                      component="a" 
                      href={ensureAbsoluteUrl(account.website)} 
                      target="_blank"
                    >
                      <ListItemIcon><WebsiteIcon /></ListItemIcon>
                      <ListItemText primary={account.website} secondary="Website" />
                    </ListItem>
                  )}
                  {account.phone && (
                    <ListItem>
                      <ListItemIcon><PhoneIcon /></ListItemIcon>
                      <ListItemText primary={account.phone} secondary="Phone" />
                    </ListItem>
                  )}
                  {account.email && (
                    <ListItem>
                      <ListItemIcon><EmailIcon /></ListItemIcon>
                      <ListItemText primary={account.email} secondary="Email" />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Billing Address */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Billing Address
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Street"
                      value={formData.billing_street}
                      onChange={handleFormChange('billing_street')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={formData.billing_city}
                      onChange={handleFormChange('billing_city')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="State/Province"
                      value={formData.billing_state}
                      onChange={handleFormChange('billing_state')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Postal Code"
                      value={formData.billing_postal_code}
                      onChange={handleFormChange('billing_postal_code')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      value={formData.billing_country}
                      onChange={handleFormChange('billing_country')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {(account.billing_address?.street || account.billing_address?.city || account.billing_address?.country) ? (
                    <ListItem>
                      <ListItemIcon><LocationIcon /></ListItemIcon>
                      <ListItemText
                        primary={[
                          account.billing_address?.street,
                          account.billing_address?.city,
                          account.billing_address?.state,
                          account.billing_address?.postal_code,
                          account.billing_address?.country,
                        ].filter(Boolean).join(', ')}
                      />
                    </ListItem>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No billing address on file
                    </Typography>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Shipping Address */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Shipping Address
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Street"
                      value={formData.shipping_street}
                      onChange={handleFormChange('shipping_street')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={formData.shipping_city}
                      onChange={handleFormChange('shipping_city')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="State/Province"
                      value={formData.shipping_state}
                      onChange={handleFormChange('shipping_state')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Postal Code"
                      value={formData.shipping_postal_code}
                      onChange={handleFormChange('shipping_postal_code')}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      value={formData.shipping_country}
                      onChange={handleFormChange('shipping_country')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {(account.shipping_address?.street || account.shipping_address?.city || account.shipping_address?.country) ? (
                    <ListItem>
                      <ListItemIcon><LocationIcon /></ListItemIcon>
                      <ListItemText
                        primary={[
                          account.shipping_address?.street,
                          account.shipping_address?.city,
                          account.shipping_address?.state,
                          account.shipping_address?.postal_code,
                          account.shipping_address?.country,
                        ].filter(Boolean).join(', ')}
                      />
                    </ListItem>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No shipping address on file
                    </Typography>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Social Links */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Social Links
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Grid container spacing={2}>
                  {/* ... Edit Mode Inputs (These are correct because formData is flattened) ... */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="LinkedIn URL"
                      value={formData.linkedin_url}
                      onChange={handleFormChange('linkedin_url')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Twitter Handle"
                      value={formData.twitter_handle}
                      onChange={handleFormChange('twitter_handle')}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Facebook URL"
                      value={formData.facebook_url}
                      onChange={handleFormChange('facebook_url')}
                    />
                  </Grid>
                </Grid>
              ) : (
                <List dense>
                  {/* FIX: Access properties via account.social object */}
                  {account.social?.linkedin_url && (
                    <ListItem button component="a" href={ensureAbsoluteUrl(account.social.linkedin_url)} target="_blank">
                      <ListItemIcon><LinkedInIcon /></ListItemIcon>
                      <ListItemText primary="LinkedIn" />
                    </ListItem>
                  )}
                  
                  {account.social?.twitter_handle && (
                    <ListItem 
                      button 
                      component="a" 
                      // FIX: Access via account.social.twitter_handle
                      href={`https://twitter.com/${account.social.twitter_handle.replace(/^@/, '')}`} 
                      target="_blank"
                    >
                      <ListItemIcon><TwitterIcon /></ListItemIcon>
                      <ListItemText primary={`@${account.social.twitter_handle.replace(/^@/, '')}`} />
                    </ListItem>
                  )}
                  
                  {account.social?.facebook_url && (
                    <ListItem button component="a" href={ensureAbsoluteUrl(account.social.facebook_url)} target="_blank">
                      <ListItemIcon><FacebookIcon /></ListItemIcon>
                      <ListItemText primary="Facebook" />
                    </ListItem>
                  )}
                  
                  {!account.social?.linkedin_url && !account.social?.twitter_handle && !account.social?.facebook_url && (
                    <Typography color="text.secondary" variant="body2">
                      No social links on file
                    </Typography>
                  )}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Tags & Description */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Tags
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {tags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      onClick={() => handleTagToggle(tag.name)}
                      color={formData.tags?.includes(tag.name) ? 'primary' : 'default'}
                      variant={formData.tags?.includes(tag.name) ? 'filled' : 'outlined'}
                      sx={{ mb: 1 }}
                    />
                  ))}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {account.tags?.length > 0 ? (
                    account.tags.map((tag, index) => (
                      <Chip key={index} label={tag} size="small" />
                    ))
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      No tags assigned
                    </Typography>
                  )}
                </Stack>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Description
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.description}
                  onChange={handleFormChange('description')}
                  placeholder="Add notes about this account..."
                />
              ) : (
                <Typography variant="body2" color={account.description ? 'text.primary' : 'text.secondary'}>
                  {account.description || 'No description'}
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Contacts Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="h6">Associated Contacts ({account.contacts?.length || 0})</Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts?account_id=${accountId}`)}
          >
            View All Contacts
          </Button>
        </Box>

        {account.contacts?.length > 0 ? (
          <Grid container spacing={2}>
            {account.contacts.slice(0, 6).map((contact) => (
              <Grid item xs={12} sm={6} md={4} key={contact.id}>
                <Card>
                  <CardActionArea
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts/${contact.id}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {(contact.first_name?.[0] || '') + (contact.last_name?.[0] || '')}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1">
                            {[contact.first_name, contact.last_name].filter(Boolean).join(' ')}
                          </Typography>
                          {contact.job_title && (
                            <Typography variant="body2" color="text.secondary">
                              {contact.job_title}
                            </Typography>
                          )}
                          {contact.email && (
                            <Typography variant="body2" color="text.secondary">
                              {contact.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No contacts associated with this account</Typography>
          </Paper>
        )}
      </TabPanel>

      {/* Hierarchy Tab */}
      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          {/* Parent Account */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Parent Account
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {editMode ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Parent Account</InputLabel>
                  <Select
                    value={formData.parent_account_id}
                    label="Parent Account"
                    onChange={handleFormChange('parent_account_id')}
                  >
                    <MenuItem value="">None</MenuItem>
                    {allAccounts.map((acc) => (
                      <MenuItem key={acc.id} value={acc.id}>
                        {acc.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : account.parent_account ? (
                <Card>
                  <CardActionArea
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients/${account.parent_account.id}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <HierarchyIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1">{account.parent_account.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Parent Account
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No parent account
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Child Accounts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Child Accounts ({account.child_accounts?.length || 0})
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {account.child_accounts?.length > 0 ? (
                <List>
                  {account.child_accounts.map((child) => (
                    <ListItem
                      key={child.id}
                      button
                      onClick={() => navigate(`/chatbot/${chatbotId}/crm/clients/${child.id}`)}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <BusinessIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={child.name}
                        secondary={AccountTypeLabels[child.account_type]}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="text.secondary" variant="body2">
                  No child accounts
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Activities Tab */}
      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="h6">Activity Timeline</Typography>
          {canEdit && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setActivityDialogOpen(true)}
            >
              Log Activity
            </Button>
          )}
        </Box>

        {account.activities?.length > 0 ? (
          <List>
            {account.activities.map((activity) => (
              <ListItem key={activity.id} alignItems="flex-start" divider
                secondaryAction={
                  canEdit && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleEditActivity(activity)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteActivityId(activity.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )
                }
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: 'primary.main' }}>
                    {getActivityIcon(activity.activity_type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">{activity.subject}</Typography>
                      <Chip
                        label={ActivityTypeLabels[activity.activity_type]}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {activity.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {formatDate(activity.activity_date)}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
            ))}
          </List>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No activities recorded</Typography>
            {canEdit && (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setActivityDialogOpen(true)}
                sx={{ mt: 2 }}
              >
                Log First Activity
              </Button>
            )}
          </Paper>
        )}
      </TabPanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{account.name}</strong>?
            This action will archive the account.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Activity Dialog */}
      <Dialog
        open={activityDialogOpen}
        onClose={() => { setActivityDialogOpen(false); resetActivityForm(); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingActivity ? 'Edit Activity' : 'Log Activity'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Activity Type</InputLabel>
                <Select
                  value={activityForm.activity_type}
                  label="Activity Type"
                  onChange={handleActivityFormChange('activity_type')}
                >
                  {Object.values(ActivityType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {ActivityTypeLabels[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                value={activityForm.subject}
                onChange={handleActivityFormChange('subject')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={activityForm.description}
                onChange={handleActivityFormChange('description')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Date & Time"
                value={activityForm.activity_date}
                onChange={handleActivityFormChange('activity_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Email-specific fields */}
            {activityForm.activity_type === ActivityType.EMAIL && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="From" value={activityForm.email_from}
                    onChange={handleActivityFormChange('email_from')} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="To" value={activityForm.email_to}
                    onChange={handleActivityFormChange('email_to')} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="CC" value={activityForm.email_cc}
                    onChange={handleActivityFormChange('email_cc')} />
                </Grid>
              </>
            )}

            {/* Call-specific fields */}
            {activityForm.activity_type === ActivityType.CALL && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Direction</InputLabel>
                    <Select value={activityForm.call_direction} label="Direction"
                      onChange={handleActivityFormChange('call_direction')}>
                      <MenuItem value="inbound">Inbound</MenuItem>
                      <MenuItem value="outbound">Outbound</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Result" value={activityForm.call_result}
                    onChange={handleActivityFormChange('call_result')}
                    placeholder="e.g. Connected, Voicemail" />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Duration (min)" type="number"
                    value={activityForm.duration_minutes}
                    onChange={handleActivityFormChange('duration_minutes')} />
                </Grid>
              </>
            )}

            {/* Task-specific fields */}
            {activityForm.activity_type === ActivityType.TASK && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField fullWidth type="datetime-local" label="Due Date"
                    value={activityForm.task_due_date}
                    onChange={handleActivityFormChange('task_due_date')}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select value={activityForm.task_priority} label="Priority"
                      onChange={handleActivityFormChange('task_priority')}>
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActivityDialogOpen(false); resetActivityForm(); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddActivity}
            disabled={!activityForm.subject}
          >
            {editingActivity ? 'Update Activity' : 'Log Activity'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Activity Confirmation */}
      <Dialog open={Boolean(deleteActivityId)} onClose={() => setDeleteActivityId(null)}>
        <DialogTitle>Delete Activity</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this activity? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteActivityId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => handleDeleteActivity(deleteActivityId)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountDetail;